import { Box } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  BillingDocumentTypes,
  DeprecatedDocumentTypesArr,
  DocumentStatus,
  DocumentTypesArr,
  DoePrDocumentTypesArr,
  GeneratorDocumentTypesArr,
  GeothermalDesignDocumentTypesArr,
  GeothermalDocumentTypesArr,
  HVACDocumentTypesArr,
  InstallDocumentTypes,
  InstallDocumentTypesArr,
  ProgramType,
  WaterHeaterDocumentTypesArr,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { ActivationMilestonePackageContainer } from '../ActivationMilestonePackage/ActivationMilestonePackageContainer';
import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import CancelAccount from '../Cancel/CancelAccount';
import { Contracts } from '../Contracts/ContractList';
import { Documents } from '../Documents';
import { InstallMilestonePackageContainer } from '../InstallMilestonePackage/InstallMilestonePackageContainer';
import { LoopFieldMilestonePackageContainer } from '../LoopFieldMilestonePackage/LoopFieldMilestonePackageContainer';
import { PostActivationMilestonePackageContainer } from '../PostActivationMilestonePackage/PostActivationMilestonePackagerContainer';
import { Quotes } from '../Quotes/Quotes';
import AdminStipulation from '../Stipulations/AdminStipulation';
import { Tasks } from '../Tasks';
import { Underwriting } from '../Underwriting/Underwriting';

import AccountBanners from './AccountBanners';
import { AccountDetails } from './AccountDetails';
import { AccountMilestones } from './AccountMilestones';

const INSTALL_DOCUMENTS_TO_FETCH = [
  {
    types: InstallDocumentTypesArr,
    status: [DocumentStatus.approved, DocumentStatus.rejected],
  },
];

const Overview = () => {
  const { id } = useParams<{ id: any }>();
  const { adminStipulations, enableCancelAccountButton, viewActivationMilestonePackage } = useFlags();
  const { data: account } = useGetAccountQuery(id);
  const programType = account?.programType;

  const { claims } = useAuth();

  const documentFilters = useMemo(() => {
    let documentFilters: any[] = isPalmettoFinanceUser(claims)
      ? [...DocumentTypesArr, BillingDocumentTypes.incomingDirectPayInvoice, InstallDocumentTypes.invoice]
      : [...DocumentTypesArr, InstallDocumentTypes.invoice];

    switch (programType) {
      case ProgramType.doePr:
        documentFilters = [...documentFilters, ...DoePrDocumentTypesArr];
        break;
      case ProgramType.hvac:
        documentFilters = [...documentFilters, ...HVACDocumentTypesArr];
        break;
      case ProgramType.waterHeater:
        documentFilters = [...documentFilters, ...WaterHeaterDocumentTypesArr];
        break;
      case ProgramType.generator:
        documentFilters = [...documentFilters, ...GeneratorDocumentTypesArr];
        break;
      case ProgramType.geothermal:
        documentFilters = [...documentFilters, ...GeothermalDocumentTypesArr, ...GeothermalDesignDocumentTypesArr];
        break;
    }

    return documentFilters;
  }, [programType, claims]);

  const isNonHvac = programType !== ProgramType.hvac;
  const isNonWaterHeater = programType !== ProgramType.waterHeater;
  const isNonGeothermal = programType !== ProgramType.geothermal;
  const isNonGenerator = programType !== ProgramType.generator;
  const showPostActivationPackage =
    programType === ProgramType.doePr ||
    (programType === ProgramType.solar && account?.address?.state?.toUpperCase() === 'PR');

  return (
    <Box childGap="lg">
      <AccountBanners accountId={id} />
      <AccountMilestones />
      <Box
        direction={{ base: 'column', desktop: 'row' }}
        alignItems="flex-start"
        childGap={{ base: 'lg', desktop: '3xl' }}
        width="100"
      >
        <Box width={{ base: '100', desktop: '30' }}>
          <AccountDetails />
          <Box direction="row" gap="sm">
            {enableCancelAccountButton && <CancelAccount />}
            {adminStipulations && <AdminStipulation />}
          </Box>
        </Box>
        <Box width={{ base: '100', desktop: '70' }} childGap="lg">
          <Tasks />
          <Box>
            <Underwriting />
          </Box>
          <Quotes />
          <Contracts />

          <Documents
            documentTypeFilter={[...documentFilters, ...DeprecatedDocumentTypesArr]}
            additionalDocumentsToFetch={INSTALL_DOCUMENTS_TO_FETCH}
            programType={programType}
          />
          {!isNonGeothermal && <LoopFieldMilestonePackageContainer />}
          <InstallMilestonePackageContainer />
          {viewActivationMilestonePackage && isNonHvac && isNonWaterHeater && isNonGeothermal && isNonGenerator && (
            <ActivationMilestonePackageContainer />
          )}
          {showPostActivationPackage && <PostActivationMilestonePackageContainer />}
        </Box>
      </Box>
    </Box>
  );
};

export default Overview;
